import type { CurrentUser } from "@procuremate/shared";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout, FormError } from "@/components/layout/auth-layout";
import { api } from "@/lib/api-client";
import { useSetCurrentUser } from "./api";
import { authErrorMessage } from "./error-message";
import { SetPasswordForm } from "./set-password-form";

/** Landing page of the invite email: set the first password, then go straight in. */
export function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setCurrentUser = useSetCurrentUser();
  const token = params.get("token") ?? "";

  const accept = useMutation({
    mutationFn: (password: string) => api<CurrentUser>("/auth/invite/accept", { body: { token, password } }),
    onSuccess: (user) => {
      setCurrentUser(user);
      navigate("/", { replace: true });
    },
  });

  return (
    <AuthLayout title={t("auth.inviteTitle")} subtitle={t("auth.inviteSubtitle")}>
      {token ? (
        <SetPasswordForm
          submitLabel={t("auth.inviteSubmit")}
          pending={accept.isPending}
          serverError={authErrorMessage(accept.error, t)}
          onSubmit={(password) => accept.mutate(password)}
        />
      ) : (
        <>
          <FormError message={t("auth.invalidLink")} />
          <Link to="/login" className="mt-4 block text-sm text-link underline-offset-4 hover:underline">
            {t("auth.backToSignIn")}
          </Link>
        </>
      )}
    </AuthLayout>
  );
}
